/** SessionStorage clipboard for medicine add form (text + dropdowns only; no images). */

export const MEDICINE_FORM_CLIPBOARD_VERSION = 1 as const;

export type MedicineFormClipboardPayload = {
	v: typeof MEDICINE_FORM_CLIPBOARD_VERSION;
	fields: Record<string, unknown>;
};

const TRIMMED_CLIPBOARD_FIELDS = [
	'medicineName',
	'medicineStrength',
	'packSize',
	'doseFormId',
	'categoryId',
	'subCategoryId',
	'productCode',
];

const IMAGE_OR_FILE_KEYS = new Set([
	'primaryImage',
	'secondaryImage',
	'medicinePrimaryImageUrl',
	'medicineSecondaryImageUrl',
]);

/** Strip server / read-only keys that may appear via initialValues spread from API rows */
const SERVER_KEYS = new Set([
	'id',
	'qualityCheck',
	'createdBy',
	'updatedBy',
	'createdAt',
	'updatedAt',
	'imageUploadedBy',
	'imageUploadedAt',
	'approvedByUser',
	'Warnings',
	'description', // legacy; form uses shortDescription
]);

export const getMedicineClipboardStorageKey = (): string => {
	try {
		const companyId = localStorage.getItem('companyId');
		return `medPanel.medicineFormClipboard.${companyId || 'default'}`;
	} catch {
		return 'medPanel.medicineFormClipboard.default';
	}
};

function normalizeLeaf(val: unknown): unknown {
	if (val === null || val === undefined) return val;
	if (typeof val === 'object' && val !== null && 'value' in val && 'label' in val) {
		return (val as { value: unknown }).value;
	}
	if (Array.isArray(val)) {
		return val.map((item) => {
			if (item && typeof item === 'object' && 'name' in item && 'strength' in item) {
				return {
					name: (item as { name?: string }).name ?? '',
					strength: (item as { strength?: string }).strength ?? '',
				};
			}
			return normalizeLeaf(item);
		});
	}
	return val;
}

/** Build a JSON-safe clipboard payload from Ant Design form values */
export function buildMedicineClipboardPayload(
	values: Record<string, unknown>
): MedicineFormClipboardPayload {
	const fields: Record<string, unknown> = {};
	for (const [key, v] of Object.entries(values)) {
		if (IMAGE_OR_FILE_KEYS.has(key) || SERVER_KEYS.has(key)) continue;
		fields[key] = normalizeLeaf(v);
	}
	return { v: MEDICINE_FORM_CLIPBOARD_VERSION, fields };
}

export function saveMedicineClipboardPayload(payload: MedicineFormClipboardPayload): void {
	sessionStorage.setItem(getMedicineClipboardStorageKey(), JSON.stringify(payload));
}

export function loadMedicineClipboardPayload(): MedicineFormClipboardPayload | null {
	try {
		const raw = sessionStorage.getItem(getMedicineClipboardStorageKey());
		if (!raw) return null;
		const parsed = JSON.parse(raw) as MedicineFormClipboardPayload;
		if (
			!parsed ||
			parsed.v !== MEDICINE_FORM_CLIPBOARD_VERSION ||
			typeof parsed.fields !== 'object' ||
			parsed.fields === null
		) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

export function normalizeClipboardFieldsForPaste(
	fields: Record<string, unknown>
): Record<string, unknown> {
	const normalizedFields: Record<string, unknown> = { ...fields };

	for (const key of TRIMMED_CLIPBOARD_FIELDS) {
		const value = normalizedFields[key];
		if (typeof value === 'string') {
			normalizedFields[key] = value.trim();
		}
	}

	if (typeof normalizedFields.medicineName === 'string') {
		normalizedFields.medicineName = normalizedFields.medicineName.replace(/\s+/g, ' ');
	}

	if (typeof normalizedFields.weightage === 'string') {
		const normalizedWeightage = normalizedFields.weightage.trim();
		normalizedFields.weightage = normalizedWeightage === '' ? undefined : normalizedWeightage;
	}

	return normalizedFields;
}
